import { Button } from "../ui/button";
import { AuthSeparator } from "./auth-separator";

export interface SsoButtonsProvider {
  connectionId: string;
  providerType: string;
  displayName: string;
  iconUrl?: string;
}

interface SsoButtonsProps {
  providers: SsoButtonsProvider[];
  onSelect: (provider: SsoButtonsProvider) => void;
  isLoading?: boolean;
  separatorLabel?: string;
}

export function SsoButtons({
  providers,
  onSelect,
  isLoading = false,
  separatorLabel = "Or continue with",
}: SsoButtonsProps) {
  if (providers.length === 0) return null;

  const sideBySide = providers.length === 2;

  return (
    <div className="space-y-4">
      <div
        className={
          sideBySide
            ? "grid grid-cols-1 gap-2 sm:grid-cols-2"
            : "flex flex-col gap-2"
        }
      >
        {providers.map((p) => (
          <Button
            key={p.connectionId}
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => onSelect(p)}
            disabled={isLoading}
          >
            <ProviderIcon provider={p} />
            <span>{p.displayName}</span>
          </Button>
        ))}
      </div>
      <AuthSeparator label={separatorLabel} />
    </div>
  );
}

function ProviderIcon({ provider }: { provider: SsoButtonsProvider }) {
  if (provider.iconUrl) {
    return (
      <img src={provider.iconUrl} alt="" className="h-4 w-4" aria-hidden="true" />
    );
  }
  const key = `${provider.connectionId} ${provider.providerType} ${provider.displayName}`.toLowerCase();
  if (key.includes("google")) return <GoogleIcon />;
  if (key.includes("microsoft") || key.includes("azure") || key.includes("entra")) {
    return <MicrosoftIcon />;
  }
  return <KeyIcon />;
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.5 14.6 2.5 12 2.5 6.8 2.5 2.6 6.7 2.6 12s4.2 9.5 9.4 9.5c5.4 0 9-3.8 9-9.2 0-.6-.1-1.1-.2-1.6H12z"
      />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#F25022" d="M3 3h8.5v8.5H3z" />
      <path fill="#7FBA00" d="M12.5 3H21v8.5h-8.5z" />
      <path fill="#00A4EF" d="M3 12.5h8.5V21H3z" />
      <path fill="#FFB900" d="M12.5 12.5H21V21h-8.5z" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="7.5" cy="15.5" r="3.5" />
      <path d="M10 13l9-9" />
      <path d="M16 7l3 3" />
      <path d="M19 4l2 2" />
    </svg>
  );
}
